import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useFormHook } from '@Hooks/index';
import { EddEmbeddedFields } from '@Pages/happyFlowPages/eddQuestions/pages/embedded/fields.types';
import {
  getDefaultValueByQuestionId,
  saveEddQuestionToLocalStore,
} from '@Pages/happyFlowPages/eddQuestions/shared.utils';
import { EDD_VERSION } from '@Pages/happyFlowPages/eddQuestions/consts';
import { useBoundStore } from '@Store/useBoundStore';
import { useNavigationManager } from '@Router/useNavigationManager';
import { usePostEddData } from '@Pages/happyFlowPages/eddQuestions/hooks/usePostEddData';
import { RoutesMap } from '@Router/router.types.ts';

const minLengthUrl = 5;
const maxLengthUrl = 200;

export const useNonprofitSocialMediaLogic = () => {
  const { mutateAsync: postEddData, isPending: isPostingEddData } = usePostEddData();
  const { getEddEmbeddedQuestions } = useBoundStore((state) => state);
  const { navigateToNextPage } = useNavigationManager();
  const { t } = useTranslation('eddQuestions');

  const formMethods = useFormHook({
    schemaTitle: 'Nonprofit Social Media',
    formDefaultValues: {
      [EddEmbeddedFields.EDD_NONPROFIT_WEBSITE]:
        getDefaultValueByQuestionId(EddEmbeddedFields.EDD_NONPROFIT_WEBSITE) || '',
    },
    formSchema: z.object({
      [EddEmbeddedFields.EDD_NONPROFIT_WEBSITE]: z
        .string()
        .trim()
        .min(1, { message: t('organic.websiteSocial.validation.invalidUrl') })
        .min(minLengthUrl, {
          message: t('organic.websiteSocial.validation.minLengthUrl', {
            minLength: minLengthUrl,
          }),
        })
        .max(maxLengthUrl, {
          message: t('organic.websiteSocial.validation.maxLengthUrl', {
            maxLength: maxLengthUrl,
          }),
        }),
    }),
  });

  const handleSubmit = async () => {
    saveEddQuestionToLocalStore({
      question: t('organic.NonProfitSocialMedia.websiteQuestion'),
      questionId: EddEmbeddedFields.EDD_NONPROFIT_WEBSITE,
      answer: formMethods.getValues(EddEmbeddedFields.EDD_NONPROFIT_WEBSITE),
    });

    try {
      await postEddData({
        version: EDD_VERSION,
        results: getEddEmbeddedQuestions(),
      });
      navigateToNextPage(RoutesMap.EDD_NONPROFIT_SOCIAL_MEDIA);
    } catch (error) {
      console.error(error);
    }
  };

  return {
    formMethods,
    handleSubmit,
    isLoading: isPostingEddData,
  };
};
